<div
  class="export-bar"
  role="region"
  [attr.aria-label]="'consultant.bottomNav.region' | translate"
  data-profile-search-ignore
>
  <div class="export-bar__left">
    <span class="export-bar__count">{{ selectedCount() }}</span>
    <div class="export-bar__selection" aria-live="polite">
      <b class="export-bar__label">{{
        'consultant.bottomNav.selected' | translate
      }}</b>
      @if (showPreselectionHint() && !twoPagerMode()) {
        <span class="export-bar__preselection-hint">{{
          'consultant.bottomNav.preselectionHint' | translate
        }}</span>
      }
    </div>

    @if (configurations().length > 0) {
      <div class="export-bar__configs">
        <mat-select
          class="export-bar__config-select"
          [value]="selectedConfigId()"
          [placeholder]="'consultant.bottomNav.loadConfig' | translate"
          [attr.aria-label]="'consultant.bottomNav.loadConfig' | translate"
          (selectionChange)="configSelected.emit($event.value)"
        >
          @for (config of configurations(); track config.id) {
            <mat-option [value]="config.id">{{ config.name }}</mat-option>
          }
        </mat-select>
        @if (selectedConfigId()) {
          <button
            mat-icon-button
            type="button"
            class="export-bar__delete-config"
            [matTooltip]="'consultant.bottomNav.deleteConfig' | translate"
            [attr.aria-label]="'consultant.bottomNav.deleteConfig' | translate"
            (click)="deleteConfig.emit()"
          >
            <mat-icon>delete</mat-icon>
          </button>
        }
      </div>
    }
  </div>

  <div class="export-bar__right">
    <div
      class="export-bar__lang"
      role="radiogroup"
      [attr.aria-label]="'consultant.export.language.label' | translate"
    >
      @for (option of languageOptions; track option) {
        <button
          type="button"
          role="radio"
          [class.export-bar__lang-option--on]="language() === option"
          [attr.aria-checked]="language() === option"
          [attr.aria-label]="'languages.' + option | translate"
          (click)="chooseLanguage(option)"
        >
          {{ option.toUpperCase() }}
        </button>
      }
    </div>

    @if (twoPagerMode()) {
      <div
        class="export-bar__pagecount"
        role="group"
        [attr.aria-label]="'consultant.twoPager.pageCount' | translate"
      >
        @for (count of pageCountOptions; track count) {
          <button
            type="button"
            [class.export-bar__pagecount-option--on]="pageCount() === count"
            [attr.aria-pressed]="pageCount() === count"
            (click)="pageCountChange.emit(count)"
          >
            {{ count }}
          </button>
        }
      </div>
    }

    <button
      type="button"
      class="export-bar__tool"
      [class.export-bar__tool--on]="twoPagerMode()"
      [attr.aria-pressed]="twoPagerMode()"
      (click)="twoPagerModeChange.emit(true)"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="3" fill="currentColor" />
        <path
          d="M10 17V7h3.2a3 3 0 0 1 0 6H10"
          fill="none"
          class="export-bar__icon-letter"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <span class="export-bar__tool-label">{{
        'consultant.bottomNav.pdfExport' | translate
      }}</span>
    </button>

    <button
      type="button"
      class="export-bar__tool"
      [class.export-bar__tool--on]="!twoPagerMode()"
      [attr.aria-pressed]="!twoPagerMode()"
      (click)="twoPagerModeChange.emit(false)"
    >
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
        <path d="M14 3v5h5M9 13h6M9 17h6M9 9h2" />
      </svg>
      <span class="export-bar__tool-label">{{
        'consultant.bottomNav.profileExport' | translate
      }}</span>
    </button>

    <button
      type="button"
      class="export-bar__tool"
      [class.export-bar__tool--on]="includeProfilePicture()"
      [attr.aria-pressed]="includeProfilePicture()"
      (click)="pictureToggle.emit()"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
        <circle cx="12" cy="8" r="4.2" />
        <path d="M3.5 21a8.5 8.5 0 0 1 17 0Z" />
      </svg>
      <span class="export-bar__tool-label">{{
        'consultant.bottomNav.profilePicture' | translate
      }}</span>
    </button>

    <span class="export-bar__sep" aria-hidden="true"></span>

    <button
      type="button"
      class="export-bar__tool export-bar__tool--action"
      [disabled]="running()"
      [matTooltip]="'consultant.bottomNav.download' | translate"
      [attr.aria-label]="'consultant.bottomNav.download' | translate"
      (click)="download.emit()"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
        <path d="M11 3h2v9.2l3.3-3.3 1.4 1.4L12 16l-5.7-5.7 1.4-1.4 3.3 3.3Z" />
        <rect x="4" y="18.5" width="16" height="2" rx="1" />
      </svg>
      <span class="export-bar__tool-label">{{
        'consultant.bottomNav.download' | translate
      }}</span>
    </button>

    <button
      type="button"
      class="export-bar__tool export-bar__tool--action"
      [disabled]="running()"
      [matTooltip]="'consultant.bottomNav.sendToTeams' | translate"
      [attr.aria-label]="'consultant.bottomNav.sendToTeams' | translate"
      (click)="sendToTeams.emit()"
    >
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M21 3 10 14" />
        <path d="M21 3 14.5 21 10 14 3 9.5Z" />
      </svg>
      <span class="export-bar__tool-label">{{
        'consultant.bottomNav.sendToTeams' | translate
      }}</span>
    </button>

    <span class="export-bar__sep" aria-hidden="true"></span>

    <button
      type="button"
      class="export-bar__tool export-bar__tool--action"
      (click)="cancel.emit()"
    >
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
      >
        <path d="M6 6l12 12M18 6 6 18" />
      </svg>
      <span class="export-bar__tool-label">{{
        'general.cancel' | translate
      }}</span>
    </button>
  </div>

  <ng-content></ng-content>
</div>
